// 首页卡片图 URL 的「共享纯函数」单一事实源（零 import）。
//
// 这些逻辑同时被两处使用，必须保持字节级一致，否则 SW 侧预取的图 URL 与页面
// <img> 实际请求的 URL 不同 → HTTP 缓存不命中、预取全部白费：
//   - 主工程：utils/img.ts 的 transformImage / transferWebP2GIF /
//     getAnimatedImageDisplayUrl 委托本文件（首页卡片经它们算出 img src）。
//   - SW lane：packages/sw-prefetch/src/prefetch-sw.ts 在首页列表预热响应到达后，
//     用 extractHomeCardImageUrls 解析出首屏卡片图 URL，回发给页面预热
//     （经 esbuild 内联进 public/sw.js，与 prefetch-headers.ts 同为
//     「SW lane 仅引零 import 纯字面量文件」约定的成员）。
//
// 零依赖（不 import 任何模块/类型）：入参宽松接收 unknown/string，
// 避免 SW 独立类型检查（tsconfig.sw.json）牵连主工程类型图。

/** 首页卡片图的 CDN resize 宽度（与 constant/img.ts DEFAULT_CHARACTER_LIST_IMAGE_WIDTH 同值）。
 * 定义在此（零依赖底层）供 SW lane 使用；constant/img.ts 由本文件派生再导出。 */
export const HOME_CARD_IMAGE_WIDTH = 400

/** 首页卡片图质量（utils/img.ts 各变换的默认值）。 */
export const HOME_CARD_IMAGE_QUALITY = 90

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov']

/** 视频 URL 判定（utils/img.ts isVideoUrl 的规范实现，host 侧委托本函数）。 */
export const isVideoUrlShared = (url: string): boolean => {
  if (!url) return false
  try {
    const pathname = new URL(url).pathname.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext))
  } catch {
    // 相对 URL / protocol-relative URL 无法被无 base 的 URL 构造器解析。
    // 保留页面 isVideoUrl 的既有兜底语义，确保页面渲染与 SW 预热分类一致。
    const normalizedUrl = url.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => normalizedUrl.includes(ext))
  }
}

/**
 * tipsy CDN 图片变换：在 host 与 path 之间插入 /cdn-cgi/image/width=...,quality=...,f=auto。
 * 与 utils/img.ts 的 transformImage / transferWebP2GIF 的 URL 构造完全一致
 * （二者 host 侧已委托本函数）。视频 URL / 空串 / 非 tipsy 域 / 已含 cdn-cgi 段的
 * URL 原样返回。
 */
export const transformTipsyImage = (
  url: string,
  width?: number,
  quality = 90
): string => {
  if (!url) return url
  if (isVideoUrlShared(url)) return url
  // eslint-disable-next-line no-useless-escape
  const regex = /(https?:\/\/[^\/]+\.tipsy\.chat)(?!.*\/cdn-cgi\/image\/)(\/.*)/
  const transWidth = width ? `width=${width},` : ''
  const newPath = `/cdn-cgi/image/${transWidth}quality=${quality},f=auto`
  const match = url.match(regex)
  if (match) {
    return `${match[1]}${newPath}${match[2]}`
  }
  return url
}

/**
 * 单张首页卡片的「图片」URL（与卡片组件的 imageMediaUrl 同算法）：
 *   - animated 是视频 → 卡片渲染 <video>，无图可预取，返回 null
 *     （不预取整段视频，太重；poster 方案是 N2 的独立话题）。
 *   - animated 是动图 → 变换后的动图地址。
 *   - 否则 → 变换后的静态封面（image_url）。
 * 镜像 utils/img.ts getAnimatedImageDisplayUrl 在 width=400/quality=90 下的行为。
 */
export const buildHomeCardImageUrl = (
  animatedUrl: string,
  fallbackUrl: string
): string | null => {
  if (animatedUrl) {
    if (isVideoUrlShared(animatedUrl)) return null
    return transformTipsyImage(
      animatedUrl,
      HOME_CARD_IMAGE_WIDTH,
      HOME_CARD_IMAGE_QUALITY
    )
  }
  if (!fallbackUrl) return null
  const transformed = transformTipsyImage(
    fallbackUrl,
    HOME_CARD_IMAGE_WIDTH,
    HOME_CARD_IMAGE_QUALITY
  )
  return transformed || null
}

/**
 * 从首页列表接口（recommend_feed/list 或 character/get/public_list）的响应 JSON
 * 里解析出前 count 张卡片的媒体 URL（去重）：
 *   - images：图片/动图卡 → cdn-cgi 变换后的 URL（与页面 <img> src 一致）
 *   - videos：视频卡 → animated_image_url 原样（页面 <video src> 不做变换，天然一致）
 * 两者各自用真实元素预热：图片用 new Image() 保留 Accept，视频用隐藏
 * <video preload=auto> 产生正确媒体请求；根 SW 再把完整响应持久化 30 天。
 *
 * 兼容两种条目形态（与 hooks/useHomeCharacterList.ts 的消费逻辑同口径）：
 *   - For You 包装形：{ type: 'character'|'story', data: {...} }
 *   - public_list 平铺形：{ character: {...}, stats, creator } 或 StoryGetRes 平铺
 * 角色媒体字段在 .character 上；story 媒体字段在条目自身。
 * 任何形状不符都静默跳过——解析是纯增益，绝不抛错。
 */
export const extractHomeCardMediaUrls = (
  json: unknown,
  count: number
): { images: string[]; videos: string[] } => {
  const images: string[] = []
  const videos: string[] = []
  if (count <= 0) return { images, videos }
  const list = (json as { data?: { list?: unknown[] } } | null | undefined)
    ?.data?.list
  if (!Array.isArray(list)) return { images, videos }
  let taken = 0
  for (const item of list) {
    if (taken >= count) break
    if (!item || typeof item !== 'object') continue
    const wrapped = item as {
      type?: string
      data?: unknown
      character?: unknown
    }
    const data = (
      wrapped.type === 'character' || wrapped.type === 'story'
        ? wrapped.data
        : item
    ) as { character?: unknown } | null | undefined
    if (!data || typeof data !== 'object') continue
    // 角色条目媒体在 .character；story 条目媒体字段平铺在条目自身
    const media = (data.character ?? data) as {
      animated_image_url?: unknown
      image_url?: unknown
    }
    const animated =
      typeof media.animated_image_url === 'string'
        ? media.animated_image_url
        : ''
    const fallback = typeof media.image_url === 'string' ? media.image_url : ''
    if (animated && isVideoUrlShared(animated)) {
      // 视频卡：<video src> 用原始 URL，无 cdn-cgi 变换
      if (videos.indexOf(animated) === -1) videos.push(animated)
      taken++
      continue
    }
    const url = buildHomeCardImageUrl(animated, fallback)
    if (url) {
      if (images.indexOf(url) === -1) images.push(url)
      taken++
    }
  }
  return { images, videos }
}
